import { useQuery } from '@tanstack/react-query';
import { RegisterScreen } from '@/components/RegisterScreen';
import { ChildRecords } from '@/components/ChildRecords';
import { StatusPill, formatDate, todayIso } from '@/components/live';
import { signedUrlsFor } from '@/api/documents';
import { usePickers } from '@/hooks/usePickers';
import { useRows } from '@/hooks/useResource';
import {
  AUDIT_RESULTS, AUDIT_STATUS, AUDIT_TYPES, RISK_LEVELS, SELECTION_METHODS, SELECTION_REASONS, financialYear, optionLabel,
} from '@/lib/practiceLists';

interface AuditRow {
  id: string; internal_audit_code: string | null; audit_period: string; audit_type: string; selection_method: string;
  population_size: number | null; sample_size: number | null; auditor_display: string | null; planned_from: string | null;
  planned_to: string | null; record_status: string; fmus_selected: number; fmus_audited: number; open_ncs: number; photo_count: number;
}
interface FmuRow {
  id: string; audit_fmu_code: string | null; plot_code: string | null; site_name: string | null; farmer_name: string | null; village_name: string | null;
  selection_reason: string; risk_level: string | null; audited_on: string | null; result: string; findings: string | null; closed_on: string | null;
}
interface PhotoRow { id: string; document_id: string; caption: string | null; taken_on: string | null; plot_code: string | null; audit_photo_code: string | null }

/**
 * Internal audit and FMU selection (owner, 2026-09-21: "Internal Audit FMU selection record ...
 * need to have option to add photographs"). An audit records how FMUs (plots) were chosen - random,
 * risk based, stratified - from how many; each selected FMU carries why it was chosen, when it was
 * audited, the result and any non-conformity with its corrective action; photographs are attached
 * to the audit or to one FMU.
 */
export default function InternalAuditsScreen() {
  const pick = usePickers(['users']);
  const fy = financialYear();
  return (
    <RegisterScreen<AuditRow>
      secondary={{ sum: (row) => row.fmus_selected, unit: 'FMUs', digits: 0 }}
      addLabel="Plan internal audit" exportName="internal-audits" recordLabel={(row) => `internal audit ${row.internal_audit_code ?? ''} (${row.audit_period})`}
      screenKey="plot.register" searchColumns={['internal_audit_code', 'audit_period', 'auditor_display']}
      subtitle="Internal audits: how FMUs were selected, the result for each, non-conformities and photographs."
      table="ops.internal_audits" title="Internal audits" view="internal_audits"
      columns={[
        { key: 'internal_audit_code', label: 'Ref', render: (row) => <strong>{row.internal_audit_code ?? '--'}</strong> },
        { key: 'audit_period', label: 'Period', render: (row) => row.audit_period },
        { key: 'audit_type', label: 'Type', render: (row) => optionLabel(AUDIT_TYPES, row.audit_type) },
        { key: 'selection_method', label: 'Selection', render: (row) => `${optionLabel(SELECTION_METHODS, row.selection_method)}${row.sample_size ? ` - ${row.sample_size} of ${row.population_size ?? '?'}` : ''}` },
        { key: 'fmus_selected', label: 'FMUs', align: 'right', render: (row) => `${row.fmus_audited} / ${row.fmus_selected} audited` },
        { key: 'open_ncs', label: 'Open NCs', align: 'right', render: (row) => (row.open_ncs ? <span className="field-error">{row.open_ncs}</span> : '0') },
        { key: 'photo_count', label: 'Photos', align: 'right', render: (row) => row.photo_count },
        { key: 'auditor_display', label: 'Auditor', render: (row) => row.auditor_display ?? '--' },
        { key: 'planned_from', label: 'Planned', render: (row) => (row.planned_from ? `${formatDate(row.planned_from)}${row.planned_to ? ` - ${formatDate(row.planned_to)}` : ''}` : '--') },
        { key: 'record_status', label: 'Status', render: (row) => <StatusPill value={row.record_status} /> },
      ]}
      stats={[
        { key: 'planned', label: 'Planned', tone: 'info', match: (row) => row.record_status === 'planned' },
        { key: 'progress', label: 'In progress', tone: 'warn', match: (row) => row.record_status === 'in_progress' },
        { key: 'ncs', label: 'With open NCs', tone: 'error', match: (row) => row.open_ncs > 0 },
        { key: 'done', label: 'Completed / closed', tone: 'ok', match: (row) => ['completed', 'closed'].includes(row.record_status) },
      ]}
      defaults={{ audit_period: fy.label, audit_type: 'internal', selection_method: 'random', record_status: 'planned' }}
      fields={[
        { key: 'audit_period', label: 'Audit period', type: 'text', required: true, suggestions: [fy.label], hint: 'Financial year, e.g. 2026-27.' },
        { key: 'audit_type', label: 'Type', type: 'select', options: AUDIT_TYPES, required: true },
        { key: 'selection_method', label: 'FMU selection method', type: 'select', options: SELECTION_METHODS, required: true },
        { key: 'selection_basis', label: 'Selection basis', type: 'text', placeholder: 'e.g. Square root of members (√144 = 12), stratified by block' },
        { key: 'population_size', label: 'FMUs in the group', type: 'number' },
        { key: 'sample_size', label: 'FMUs to audit', type: 'number' },
        { key: 'random_seed', label: 'Random draw reference', type: 'text', placeholder: 'e.g. RAND() seed 2026-10-01 / lottery slips', showIf: (form) => form.selection_method === 'random' || form.selection_method === 'stratified' },
        { key: 'auditor_id', label: 'Auditor', type: 'select', options: pick.users, placeholder: 'Select staff' },
        { key: 'auditor_name', label: 'Auditor (if not a user)', type: 'text', placeholder: 'e.g. External consultant name' },
        { key: 'planned_from', label: 'Planned from', type: 'date' },
        { key: 'planned_to', label: 'Planned to', type: 'date' },
        { key: 'scope', label: 'Scope', type: 'textarea', placeholder: 'e.g. FSC FM group standard, all principles; 12 FMUs in 3 blocks' },
        { key: 'summary_findings', label: 'Summary of findings', type: 'textarea', required: (form) => ['completed', 'closed'].includes(String(form.record_status)) },
        { key: 'record_status', label: 'Status', type: 'select', options: AUDIT_STATUS, required: true },
      ]}
      validate={(form) => ({
        audit_period: /^\d{4}-\d{2}$/.test(String(form.audit_period)) ? undefined : 'Write the period as 2026-27.',
        sample_size: form.sample_size && form.population_size && Number(form.sample_size) > Number(form.population_size) ? 'Cannot audit more FMUs than the group has.' : undefined,
        planned_to: form.planned_to && form.planned_from && String(form.planned_to) < String(form.planned_from) ? 'Cannot end before it starts.' : undefined,
      })}
    >
      {(audit) => <AuditChildren audit={audit} />}
    </RegisterScreen>
  );
}

function AuditChildren({ audit }: { audit: AuditRow }) {
  const pick = usePickers(['plots', 'sites']);
  const fmus = useRows<FmuRow>('internal_audit_fmus', { filters: { audit_id: audit.id } });
  const photos = useRows<PhotoRow>('internal_audit_photos', { filters: { audit_id: audit.id } });
  const ids = (photos.data ?? []).map((row) => row.document_id);
  const links = useQuery({ queryKey: ['signed-urls', ...ids], queryFn: () => signedUrlsFor(ids), enabled: ids.length > 0 });
  const fmuOptions = (fmus.data ?? []).map((row) => ({ id: row.id, label: `${row.plot_code ?? row.site_name ?? ''} ${row.farmer_name ?? ''}`.trim() }));

  return (
    <>
      <ChildRecords<FmuRow>
        addLabel="Select FMU" emptyText="No FMUs selected yet."
        fields={[
          { key: 'plot_id', label: 'FMU (plot)', type: 'select', options: pick.plots, required: (form) => !form.site_id, placeholder: 'Not a plot' },
          { key: 'site_id', label: 'Or a site', type: 'select', options: pick.sites, required: (form) => !form.plot_id, showIf: (form) => !form.plot_id,
            placeholder: 'Not a site', hint: 'Audit a sawmill, depot or seasoning unit instead of a plot.' },
          { key: 'selection_reason', label: 'Why selected', type: 'select', options: SELECTION_REASONS, required: true },
          { key: 'risk_level', label: 'Risk', type: 'select', options: RISK_LEVELS },
          { key: 'audited_on', label: 'Audited on', type: 'date', max: todayIso(), required: (form) => form.result !== 'not_audited' },
          { key: 'result', label: 'Result', type: 'select', options: AUDIT_RESULTS, required: true },
          { key: 'findings', label: 'Findings', type: 'textarea', required: (form) => ['minor_nc', 'major_nc'].includes(String(form.result)),
            placeholder: 'e.g. No PPE record for the September spraying; 2 workers without ID on file.' },
          { key: 'corrective_action', label: 'Corrective action', type: 'textarea', required: (form) => ['minor_nc', 'major_nc'].includes(String(form.result)),
            placeholder: 'e.g. PPE issue register started; IDs collected within 30 days.' },
          { key: 'corrective_action_due', label: 'Action due by', type: 'date' },
          { key: 'closed_on', label: 'NC closed on', type: 'date' },
          { key: 'remarks', label: 'Remarks', type: 'textarea' },
        ]}
        defaults={{ selection_reason: 'random_draw', result: 'not_audited' }}
        filters={{ audit_id: audit.id }} ownerId={audit.id} parent={{ audit_id: audit.id }} screenKey="plot.register"
        summary={(row) => ({
          title: `${row.plot_code ?? row.site_name ?? 'FMU'}${row.farmer_name ? ` - ${row.farmer_name}` : ''}`,
          badge: <StatusPill value={row.result} />,
          detail: [optionLabel(SELECTION_REASONS, row.selection_reason), row.risk_level ? `${row.risk_level} risk` : null, row.village_name,
            row.audited_on ? `audited ${formatDate(row.audited_on)}` : 'not audited yet', row.findings,
            row.closed_on ? `closed ${formatDate(row.closed_on)}` : null, row.audit_fmu_code].filter(Boolean).join(' · '),
        })}
        title="FMUs selected"
        validate={(form) => ({
          closed_on: form.closed_on && form.audited_on && String(form.closed_on) < String(form.audited_on) ? 'Cannot close before the audit.' : undefined,
        })}
        table="ops.internal_audit_fmus" view="internal_audit_fmus" />

      <ChildRecords<PhotoRow>
        addLabel="Add photograph" emptyText="No photographs yet."
        extra={(row) => (links.data?.[row.document_id]
          ? <a href={links.data[row.document_id]} rel="noreferrer" target="_blank"><img alt={row.caption ?? 'Audit photo'} className="thumb-small" src={links.data[row.document_id]} /></a>
          : null)}
        fields={[
          { key: 'document_id', label: 'Photograph', type: 'file', accept: 'image/*', required: true },
          { key: 'audit_fmu_id', label: 'FMU', type: 'select', options: fmuOptions, placeholder: 'The audit as a whole' },
          { key: 'caption', label: 'Caption', type: 'text', placeholder: 'e.g. Chemical store without a lock, WB_ND_1/P2' },
          { key: 'taken_on', label: 'Taken on', type: 'date', max: todayIso() },
          { key: 'latitude', label: 'Latitude', type: 'number' },
          { key: 'longitude', label: 'Longitude', type: 'number' },
        ]}
        defaults={{ taken_on: todayIso() }}
        filters={{ audit_id: audit.id }} ownerId={audit.id} parent={{ audit_id: audit.id }} screenKey="plot.register"
        summary={(row) => ({
          title: row.caption ?? 'Photograph',
          detail: [row.plot_code, row.taken_on ? formatDate(row.taken_on) : null, row.audit_photo_code].filter(Boolean).join(' · '),
        })}
        title="Photographs"
        table="ops.internal_audit_photos" view="internal_audit_photos" />
    </>
  );
}
